import { FailureReason } from "@basic-document-viewer/api/build/domain/failure-reasons";
import React from "react";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

import { ConvertingState } from "./components/converting-state/converting-state";
import {
  DocumentViewer,
  DocumentViewerOptions,
} from "./components/document-viewer/document-viewer";
import { FailedState } from "./components/failed-state/failed-state";
import { LoadingState } from "./components/loading-state/loading-state";
import { ErrorKey, UiFailureReasons } from "./domain/error-messages";
import {
  getPdfService,
  PdfStateName as PdfStatusName,
} from "./services/pdf-service";

import "./App.css";

export interface AppProps {
  /** The foundations tenant id for which a file is being previewed */
  tenantId: string;
  /** The hash of the file */
  hash: string;
  /** The encoded original URL which acts as a secure token. */
  nonDecodedOriginalUrl: string;
  /** The decoded URL from where the original file can be downloaded */
  decodedOriginalUrl: string;
  /** In case the PDF URL is known, allows for rendering the PDF immediately without polling for status */
  pdfUrl: string | undefined;
  /** The status of the file preview on page load */
  status: PdfStatusName;
  /** Controls if conversion warner banner is shown. Set to false for PDFs and sharepoint embedded conversions */
  requiredConversion: boolean;
  /** In case the preview failed, the reason explaining why */
  failureReason: FailureReason | undefined;
  /** Additional display options */
  viewerOptions: DocumentViewerOptions;
}
type ResolvedProps = AppProps & { locale: string };

export function resolveAppProps(location: string): ResolvedProps {
  const searchParams = new URL(location).searchParams;
  const tenantId = searchParams.get("tenantId");
  const hash = searchParams.get("hash");
  // Note that searchParams.get decodes query string parameters for us
  const decodedOriginalUrl = searchParams.get("originalUrl");
  if (!tenantId || !hash || !decodedOriginalUrl) {
    throw new Error("Missing required parameters");
  }

  const pdfUrl = searchParams.get("pdfUrl") ?? undefined;
  const render = searchParams.get("render") ?? "inlineOnly";
  const status =
    (searchParams.get("status") as PdfStatusName | null) ?? "loading";
  const failureReason = searchParams.get(
    "failureReason",
  ) as FailureReason | null;
  const locale = searchParams.get("locale") ?? "en_US";

  const viewerOptions = {
    canDownload: render === "inlineOnly" ? false : true,
  };

  // Grab the original non-decoded URL from the query string. This is passed
  // into the status API. Do NOT decode it as Learn's encoding is not the same
  // as Javascript's encodeURIComponent. Running it through decodeURIComponent
  // would break the AWS signature
  const nonDecodedOriginalUrl =
    location
      .split("?")[1]
      ?.split("&")
      .find((param) => param.startsWith("originalUrl="))
      ?.split("=")[1] ?? "Missing originalUrl";

  return {
    tenantId,
    hash,
    decodedOriginalUrl,
    nonDecodedOriginalUrl,
    pdfUrl,
    status,
    requiredConversion: false,
    failureReason: failureReason ?? undefined,
    viewerOptions,
    locale,
  };
}

export const App: React.FC<AppProps> = ({
  tenantId,
  hash,
  nonDecodedOriginalUrl,
  decodedOriginalUrl,
  status: initialStatus,
  pdfUrl: initialPdfUrl,
  requiredConversion: initialRequiredConversion,
  failureReason,
  viewerOptions,
}) => {
  function onDownloadOriginal() {
    window.location.assign(decodedOriginalUrl);
  }

  const [pdfStatus, setPdfStatus] = React.useState(initialStatus);
  const [pdfUrl, setPdfUrl] = React.useState(initialPdfUrl);
  const [requiredConversion, setRequiredConversion] = React.useState(
    initialRequiredConversion,
  );
  const [errorKey, setErrorKey] = React.useState<ErrorKey | undefined>(
    failureReason ?? undefined,
  );

  React.useEffect(() => {
    if (!pdfUrl && hash !== null) {
      getPdfService()
        .load(tenantId, hash, nonDecodedOriginalUrl)
        .then((pdfEmitter) => {
          pdfEmitter.on("done", (pdfUrl, requiredConversion) => {
            setRequiredConversion(requiredConversion);
            setPdfUrl(pdfUrl);
            setPdfStatus("done");
          });
          pdfEmitter.on("converting", () => setPdfStatus("converting"));
          pdfEmitter.on("failed", (failureReason: FailureReason) => {
            setPdfStatus("failed");
            setErrorKey(failureReason);
          });
        });
    }
  }, [tenantId, pdfUrl, hash, nonDecodedOriginalUrl]);

  if (pdfStatus === "converting") {
    return <ConvertingState />;
  } else if (pdfStatus === "failed") {
    return <FailedState errorKey={errorKey} />;
  } else if (pdfStatus === "done" && pdfUrl !== undefined) {
    return (
      <DocumentViewer
        file={pdfUrl}
        requiredConversion={requiredConversion}
        viewerOptions={viewerOptions}
        onDownloadOriginal={onDownloadOriginal}
        onLoadError={() => {
          setPdfStatus("failed");
          setErrorKey(UiFailureReasons.Render);
        }}
      />
    );
  } else {
    return <LoadingState />;
  }
};
